/**
 * 🛠️💰 Owner Voucher Grant Panel (Feb 2026)
 *
 * Owner-only UI for granting Deanah & Muki's currency vouchers (Echoing Pouch,
 * Aeon's Cache, Chronophage Palette) or the Ledger stub to any target user
 * (including self). Alternative to hitting POST /api/admin/currency-vouchers/grant
 * via curl.
 *
 * Gates: parent component (Profile) already checks user.role === "owner". This
 * panel also defensively 401s any non-owner via the backend.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEM_LABELS = {
  echoing_pouch_500: "💰 Echoing Pouch (500 Chits)",
  aeons_cache_5k: "🎁 Aeon's Cache (5,000 Chits)",
  chronophage_palette: "🕰️ Chronophage Palette (500,000 Chits)",
  chrono_chroniclers_ledger: "📘 The Chrono-Chronicler's Ledger (lore stub)",
};

export default function OwnerVoucherGrantPanel({ selfUserId }) {
  const [targetMode, setTargetMode] = useState("self"); // self | other
  const [targetId, setTargetId] = useState("");
  const [itemId, setItemId] = useState("echoing_pouch_500");
  const [qty, setQty] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [myInventory, setMyInventory] = useState({});

  const refreshMyInventory = async () => {
    try {
      const r = await axios.get(`${API}/currency-vouchers/inventory`);
      setMyInventory(r.data?.inventory || {});
    } catch {
      /* ignore */
    }
  };

  useEffect(() => { refreshMyInventory(); }, []);

  const onGrant = async () => {
    const finalTargetId = targetMode === "self" ? selfUserId : targetId.trim();
    if (!finalTargetId) {
      toast.error("Enter a target user_id (or switch to 'Myself').");
      return;
    }
    const q = Math.max(1, parseInt(qty, 10) || 1);
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/currency-vouchers/grant`, {
        target_user_id: finalTargetId,
        item_id: itemId,
        qty: q,
      });
      setLastResult(r.data);
      toast.success(`✅ Granted ${q}× ${ITEM_LABELS[itemId]}`, {
        description: `→ ${finalTargetId}`,
      });
      if (finalTargetId === selfUserId) await refreshMyInventory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="owner-voucher-grant-panel">
      <div className="text-[10px] uppercase tracking-widest text-amber-300/70 flex items-center gap-2">
        <span>🛠️</span>
        <span>Deanah &amp; Muki · Currency Voucher Grant</span>
      </div>

      {/* Target */}
      <div className="space-y-2">
        <div className="text-[11px] font-semibold text-neutral-300">Grant To</div>
        <div className="flex gap-2">
          <button
            data-testid="target-self-btn"
            onClick={() => setTargetMode("self")}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition ${
              targetMode === "self"
                ? "bg-amber-600 text-black"
                : "bg-neutral-800 text-neutral-400 hover:bg-neutral-700"
            }`}
          >
            🎯 Myself
          </button>
          <button
            data-testid="target-other-btn"
            onClick={() => setTargetMode("other")}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition ${
              targetMode === "other"
                ? "bg-amber-600 text-black"
                : "bg-neutral-800 text-neutral-400 hover:bg-neutral-700"
            }`}
          >
            👤 Another User
          </button>
        </div>
        {targetMode === "other" && (
          <input
            data-testid="target-user-id-input"
            type="text"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            placeholder="target user_id (e.g., user_abc123...)"
            className="w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 text-xs text-neutral-200 placeholder-neutral-600 focus:outline-none focus:border-amber-500"
          />
        )}
        {targetMode === "self" && (
          <div className="text-[10px] text-neutral-500 italic px-1">
            → <code className="text-amber-300">{selfUserId || "…"}</code>
          </div>
        )}
      </div>

      {/* Item picker */}
      <div className="space-y-2">
        <div className="text-[11px] font-semibold text-neutral-300">Voucher Type</div>
        <select
          data-testid="voucher-item-select"
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
          className="w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 text-xs text-neutral-200 focus:outline-none focus:border-amber-500"
        >
          {Object.entries(ITEM_LABELS).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
      </div>

      {/* Quantity */}
      <div className="space-y-2">
        <div className="text-[11px] font-semibold text-neutral-300">Quantity</div>
        <input
          data-testid="voucher-qty-input"
          type="number"
          min="1"
          max="9999"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          className="w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 text-xs text-neutral-200 focus:outline-none focus:border-amber-500"
        />
      </div>

      {/* Grant button */}
      <button
        onClick={onGrant}
        disabled={submitting}
        data-testid="voucher-grant-submit-btn"
        className="w-full py-3 rounded-lg text-sm font-black tracking-wider transition bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-400 hover:to-amber-600 text-black disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {submitting ? "⏳ Granting…" : "🎁 GRANT VOUCHER"}
      </button>

      {/* My current inventory (quick reference) */}
      <div className="border-t border-neutral-800 pt-3">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Your current vouchers</div>
        <div className="grid grid-cols-4 gap-2" data-testid="owner-voucher-inventory">
          {Object.keys(ITEM_LABELS).map((id) => (
            <div
              key={id}
              className="flex flex-col items-center justify-center bg-black/40 border border-neutral-800 rounded-lg py-2"
              data-testid={`owner-voucher-count-${id}`}
            >
              <div className="text-lg leading-none">{ITEM_LABELS[id].split(" ")[0]}</div>
              <div className="text-[10px] font-black text-amber-300 mt-1">×{myInventory[id] || 0}</div>
            </div>
          ))}
        </div>
      </div>

      {lastResult && (
        <div className="text-[10px] text-emerald-400 italic border-t border-neutral-800 pt-2 line-clamp-2" data-testid="voucher-last-result">
          Last: {JSON.stringify(lastResult.granted || lastResult)}
        </div>
      )}
    </div>
  );
}
